import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { Resource as AppResource } from '../types';
import type { Resource as DbResource } from '../types/database';
import { Category } from '../types';
import type { ResourceScope } from '../components/ScopeSelector';
import { normalizeResourceCategoryId } from '../utils/category';
import { getResourceSortFallbackChain, getResourceSortOrders, type ResourceSort } from '../utils/resourceSort';
import { rankResourcesBySmartSearch, type SearchMode } from '../utils/smartSearch';
import {
  aggregateResourceFacets,
  buildPageWindow,
  type ResourceFacetRow,
} from '../utils/resourceListing';
import type { CategoryL1, CategoryL2 } from '../server/categoryTaxonomy';

interface ResourceQueryOptions {
  scope: ResourceScope;
  teamId?: string;
  category?: string;
  categoryL1?: string;
  categoryL2?: string;
  tags?: string[];
  search?: string;
  searchMode?: SearchMode;
  sort?: ResourceSort;
  page?: number;
  pageSize?: number;
  favoritesOnly?: boolean;
  featuredOnly?: boolean;
}

interface ResourceFacetOptions {
  scope: ResourceScope;
  teamId?: string;
  category?: string;
  search?: string;
  searchMode?: SearchMode;
  favoritesOnly?: boolean;
  featuredOnly?: boolean;
  activeL1?: 'all' | CategoryL1;
  activeL2?: 'all' | CategoryL2;
}

type AccessibleResourceBase = Pick<
  DbResource,
  | 'id'
  | 'title'
  | 'title_zh'
  | 'description'
  | 'description_zh'
  | 'category'
  | 'url'
  | 'icon'
  | 'tags'
  | 'featured'
  | 'visibility'
  | 'team_id'
  | 'created_at'
>;

export type AccessibleResourceRow = AccessibleResourceBase &
  Partial<Pick<DbResource, 'view_count' | 'favorite_count'>> & {
    category_l1?: string;
    category_l2?: string;
    teams?: { name: string } | null;
  };

export type AccessibleResourcesResult = {
  items: AccessibleResourceRow[];
  total: number;
};

const SUPABASE_BATCH_SIZE = 1000;
const DEFAULT_PAGE_SIZE = 40;

function isPostgrestSchemaError(error: unknown): boolean {
  const e = error as { code?: unknown; message?: unknown; details?: unknown; hint?: unknown };
  const code = typeof e?.code === 'string' ? e.code : '';
  if (code.startsWith('PGRST')) return true;

  const combined = [e?.message, e?.details, e?.hint].filter(Boolean).join(' ').toLowerCase();
  return combined.includes('schema cache') || combined.includes('does not exist') || combined.includes('relationship');
}

function normalizePageSize(value: number | undefined): number {
  if (!Number.isFinite(value)) return DEFAULT_PAGE_SIZE;
  return Math.max(1, Math.floor(value as number));
}

function buildSearchPattern(rawSearch: string | undefined): string | null {
  const raw = rawSearch?.trim() || '';
  if (raw.length < 2) return null;
  const sanitized = raw
    .slice(0, 80)
    .replace(/[(),]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (sanitized.length < 2) return null;
  return `%${sanitized}%`;
}

function mapAccessibleRows(rawRows: unknown): AccessibleResourceRow[] {
  const rows = Array.isArray(rawRows) ? (rawRows as AccessibleResourceRow[]) : [];
  return rows.map(row => ({
    ...row,
    category_l1: (row as { category_l1?: string | null }).category_l1 ?? undefined,
    category_l2: (row as { category_l2?: string | null }).category_l2 ?? undefined,
  }));
}

function mapFacetRows(rawRows: unknown, supportsHierarchy: boolean): ResourceFacetRow[] {
  const rows = Array.isArray(rawRows) ? (rawRows as Array<Record<string, unknown>>) : [];
  return rows.map(row => ({
    category: typeof row.category === 'string' ? row.category : null,
    category_l1: supportsHierarchy && typeof row.category_l1 === 'string' ? row.category_l1 : null,
    category_l2: supportsHierarchy && typeof row.category_l2 === 'string' ? row.category_l2 : null,
    tags: Array.isArray(row.tags)
      ? row.tags.filter((tag): tag is string => typeof tag === 'string')
      : [],
  }));
}

function buildResourceQuery(params: {
  select: string;
  userId: string | null | undefined;
  options: ResourceQueryOptions;
  supportsHierarchy: boolean;
  sort: ResourceSort;
  includeCount: boolean;
  includeSearch: boolean;
  includeOrdering: boolean;
  searchPattern: string | null;
}) {
  if (params.options.favoritesOnly && !params.userId) return null;

  let query: any = params.includeCount
    ? supabase.from('resources').select(params.select, { count: 'exact' })
    : supabase.from('resources').select(params.select);

  query = query.eq('is_deleted', false);

  switch (params.options.scope) {
    case 'public':
      query = query.eq('visibility', 'public').eq('publish_status', 'published');
      break;
    case 'private':
      if (!params.userId) return null;
      query = query.eq('visibility', 'private').eq('owner_id', params.userId);
      break;
    case 'team':
      query = query.eq('visibility', 'team');
      if (params.options.teamId) {
        query = query.eq('team_id', params.options.teamId);
      }
      break;
    case 'all':
    default:
      break;
  }

  if (params.options.category && params.options.category !== Category.ALL) {
    query = query.eq('category', params.options.category);
  }

  if (params.supportsHierarchy && params.options.categoryL1 && params.options.categoryL1 !== 'all') {
    query = query.eq('category_l1', params.options.categoryL1);
  }

  if (params.supportsHierarchy && params.options.categoryL2 && params.options.categoryL2 !== 'all') {
    query = query.eq('category_l2', params.options.categoryL2);
  }

  if (params.options.tags && params.options.tags.length > 0) {
    query = query.contains('tags', params.options.tags);
  }

  if (params.options.featuredOnly) {
    query = query.eq('featured', true);
  }

  if (params.options.favoritesOnly && params.userId) {
    query = query.eq('resource_favorites.user_id', params.userId);
  }

  if (params.includeSearch && params.searchPattern) {
    query = query.or(
      `title.ilike.${params.searchPattern},description.ilike.${params.searchPattern},title_zh.ilike.${params.searchPattern},description_zh.ilike.${params.searchPattern}`,
    );
  }

  if (params.includeOrdering) {
    for (const order of getResourceSortOrders(params.sort)) {
      query = query.order(order.column, { ascending: order.ascending });
    }
  }

  return query;
}

function buildFacetQuery(params: {
  select: string;
  userId: string | null | undefined;
  options: ResourceFacetOptions;
  includeCount: boolean;
  includeSearch: boolean;
  searchPattern: string | null;
}) {
  if (params.options.favoritesOnly && !params.userId) return null;

  let query: any = params.includeCount
    ? supabase.from('resources').select(params.select, { count: 'exact' })
    : supabase.from('resources').select(params.select);

  query = query.eq('is_deleted', false);

  switch (params.options.scope) {
    case 'public':
      query = query.eq('visibility', 'public').eq('publish_status', 'published');
      break;
    case 'private':
      if (!params.userId) return null;
      query = query.eq('visibility', 'private').eq('owner_id', params.userId);
      break;
    case 'team':
      query = query.eq('visibility', 'team');
      if (params.options.teamId) {
        query = query.eq('team_id', params.options.teamId);
      }
      break;
    case 'all':
    default:
      break;
  }

  if (params.options.category && params.options.category !== Category.ALL) {
    query = query.eq('category', params.options.category);
  }

  if (params.options.featuredOnly) {
    query = query.eq('featured', true);
  }

  if (params.options.favoritesOnly && params.userId) {
    query = query.eq('resource_favorites.user_id', params.userId);
  }

  if (params.includeSearch && params.searchPattern) {
    query = query.or(
      `title.ilike.${params.searchPattern},description.ilike.${params.searchPattern},title_zh.ilike.${params.searchPattern},description_zh.ilike.${params.searchPattern}`,
    );
  }

  return query;
}

export function useAccessibleResources(
  userId: string | null | undefined,
  options: ResourceQueryOptions,
  config?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['accessibleResources', userId, options],
    queryFn: async (): Promise<AccessibleResourcesResult> => {
      const page = Number.isFinite(options.page) ? Math.max(1, Math.floor(options.page as number)) : 1;
      const pageSize = normalizePageSize(options.pageSize);
      const pageWindow = buildPageWindow({ page, pageSize });
      const requestedSort: ResourceSort = options.sort ?? 'latest';
      const rawSearch = options.search?.trim() || '';
      const searchPattern = buildSearchPattern(rawSearch);
      const searchMode = options.searchMode ?? 'smart';
      const shouldUseSmartRanking = searchMode === 'smart' && rawSearch.length >= 2;

      const selectBaseV2 =
        'id,title,title_zh,description,description_zh,category,category_l1,category_l2,url,icon,tags,featured,visibility,team_id,created_at';
      const selectBaseV1 =
        'id,title,title_zh,description,description_zh,category,url,icon,tags,featured,visibility,team_id,created_at';
      const favoriteJoin = options.favoritesOnly ? ',resource_favorites!inner(user_id)' : '';
      const selectCandidates: Array<{ select: string; supportsHierarchy: boolean }> = [
        { select: `${selectBaseV2},view_count,favorite_count,teams(name)${favoriteJoin}`, supportsHierarchy: true },
        { select: `${selectBaseV2},view_count,favorite_count${favoriteJoin}`, supportsHierarchy: true },
        { select: `${selectBaseV2},view_count,teams(name)${favoriteJoin}`, supportsHierarchy: true },
        { select: `${selectBaseV2},view_count${favoriteJoin}`, supportsHierarchy: true },
        { select: `${selectBaseV2}${favoriteJoin}`, supportsHierarchy: true },
        { select: `${selectBaseV1},view_count,favorite_count,teams(name)${favoriteJoin}`, supportsHierarchy: false },
        { select: `${selectBaseV1},view_count,favorite_count${favoriteJoin}`, supportsHierarchy: false },
        { select: `${selectBaseV1},view_count,teams(name)${favoriteJoin}`, supportsHierarchy: false },
        { select: `${selectBaseV1},view_count${favoriteJoin}`, supportsHierarchy: false },
        { select: `${selectBaseV1}${favoriteJoin}`, supportsHierarchy: false },
      ];

      let lastError: unknown = null;

      if (shouldUseSmartRanking) {
        for (const candidate of selectCandidates) {
          const collectedRows: AccessibleResourceRow[] = [];
          let offset = 0;
          let candidateErrored = false;

          while (true) {
            const query = buildResourceQuery({
              select: candidate.select,
              userId,
              options,
              supportsHierarchy: candidate.supportsHierarchy,
              sort: 'latest',
              includeCount: true,
              includeSearch: false,
              includeOrdering: false,
              searchPattern: null,
            });
            if (!query) return { items: [], total: 0 };

            const { data, error, count } = await query.range(offset, offset + SUPABASE_BATCH_SIZE - 1);
            if (error) {
              lastError = error;
              if (isPostgrestSchemaError(error)) {
                candidateErrored = true;
                break;
              }
              throw error;
            }

            const rows = mapAccessibleRows(data);
            collectedRows.push(...rows);
            const exactCount = typeof count === 'number' ? count : null;
            if (rows.length < SUPABASE_BATCH_SIZE) break;
            if (exactCount !== null && collectedRows.length >= exactCount) break;
            offset += SUPABASE_BATCH_SIZE;
          }

          if (candidateErrored) continue;

          const ranked = rankResourcesBySmartSearch(collectedRows as unknown as AppResource[], rawSearch);
          const items = ranked
            .slice(pageWindow.from, pageWindow.to + 1)
            .map(item => item.resource as AccessibleResourceRow);
          return {
            items,
            total: ranked.length,
          };
        }

        if (lastError) throw lastError;
        return { items: [], total: 0 };
      }

      const sortCandidates = getResourceSortFallbackChain(requestedSort);
      for (const sortCandidate of sortCandidates) {
        for (const candidate of selectCandidates) {
          const query = buildResourceQuery({
            select: candidate.select,
            userId,
            options,
            supportsHierarchy: candidate.supportsHierarchy,
            sort: sortCandidate,
            includeCount: true,
            includeSearch: true,
            includeOrdering: true,
            searchPattern,
          });
          if (!query) return { items: [], total: 0 };

          const { data, error, count } = await query.range(pageWindow.from, pageWindow.to);
          if (!error) {
            const rows = mapAccessibleRows(data);
            return {
              items: rows,
              total: typeof count === 'number' ? count : rows.length,
            };
          }

          lastError = error;
          if (!isPostgrestSchemaError(error)) {
            throw error;
          }
        }
      }

      throw lastError;
    },
    enabled: (config?.enabled ?? true) && (options.scope !== 'private' || !!userId),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useAccessibleResourceFacets(
  userId: string | null | undefined,
  options: ResourceFacetOptions,
  config?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['accessibleResourceFacets', userId, options],
    queryFn: async () => {
      const rawSearch = options.search?.trim() || '';
      const searchPattern = buildSearchPattern(rawSearch);
      const searchMode = options.searchMode ?? 'smart';
      const includeSearch = searchMode !== 'smart' && rawSearch.length >= 2;
      const favoriteJoin = options.favoritesOnly ? ',resource_favorites!inner(user_id)' : '';

      const facetSelectCandidates: Array<{ select: string; supportsHierarchy: boolean }> = [
        { select: `category,category_l1,category_l2,tags${favoriteJoin}`, supportsHierarchy: true },
        { select: `category,tags${favoriteJoin}`, supportsHierarchy: false },
      ];

      let lastError: unknown = null;
      for (const candidate of facetSelectCandidates) {
        const rows: ResourceFacetRow[] = [];
        let offset = 0;
        let candidateErrored = false;

        while (true) {
          const query = buildFacetQuery({
            select: candidate.select,
            userId,
            options,
            includeCount: true,
            includeSearch,
            searchPattern,
          });
          if (!query) {
            return aggregateResourceFacets({
              rows: [],
              activeL1: options.activeL1 ?? 'all',
              activeL2: options.activeL2 ?? 'all',
              topTagsLimit: 24,
            });
          }

          const { data, error, count } = await query.range(offset, offset + SUPABASE_BATCH_SIZE - 1);
          if (error) {
            lastError = error;
            if (isPostgrestSchemaError(error)) {
              candidateErrored = true;
              break;
            }
            throw error;
          }

          const pageRows = mapFacetRows(data, candidate.supportsHierarchy);
          rows.push(...pageRows);
          const exactCount = typeof count === 'number' ? count : null;
          if (pageRows.length < SUPABASE_BATCH_SIZE) break;
          if (exactCount !== null && rows.length >= exactCount) break;
          offset += SUPABASE_BATCH_SIZE;
        }

        if (candidateErrored) continue;

        return aggregateResourceFacets({
          rows,
          activeL1: options.activeL1 ?? 'all',
          activeL2: options.activeL2 ?? 'all',
          topTagsLimit: 24,
        });
      }

      throw lastError;
    },
    enabled: (config?.enabled ?? true) && (options.scope !== 'private' || !!userId),
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useAccessibleResourceCounts(
  userId: string | null | undefined,
  options: { scope: ResourceScope; teamId?: string },
  config?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['accessibleResourceCounts', userId, options],
    queryFn: async () => {
      const counts = Object.values(Category).reduce((acc, cat) => {
        acc[cat] = 0;
        return acc;
      }, {} as Record<Category, number>);

      let query: any = supabase
        .from('resources')
        .select('category')
        .eq('is_deleted', false);

      switch (options.scope) {
        case 'public':
          query = query.eq('visibility', 'public').eq('publish_status', 'published');
          break;
        case 'private':
          if (!userId) return counts;
          query = query.eq('visibility', 'private').eq('owner_id', userId);
          break;
        case 'team':
          query = query.eq('visibility', 'team');
          if (options.teamId) {
            query = query.eq('team_id', options.teamId);
          }
          break;
        case 'all':
        default:
          break;
      }

      const { data, error } = await query;
      if (error) throw error;

      const rows = Array.isArray(data) ? data : [];
      for (const row of rows) {
        const categoryRaw = (row as { category?: unknown }).category;
        const category = normalizeResourceCategoryId(categoryRaw);
        counts[category] += 1;
      }

      counts[Category.ALL] = Object.entries(counts).reduce((sum, [key, value]) => {
        if (key === Category.ALL) return sum;
        return sum + value;
      }, 0);

      return counts;
    },
    enabled: (config?.enabled ?? true) && (options.scope !== 'private' || !!userId),
    staleTime: 5 * 60 * 1000,
  });
}
